import { useState } from "react";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Tabs } from "@/shared/components/ui/Tabs";
import { NotificationsList } from "../components/NotificationsList";
import {
  useMarkAllNotificationsAsRead,
  useNotifications,
} from "../hooks/notificationQueries";

const filters = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
] as const;

export function NotificationsPage() {
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const { data: notifications = [], isPending, isFetching, error, refetch } =
    useNotifications(filter === "unread");
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const hasUnread = notifications.some((notification) => !notification.read);
  const canMarkAll = hasUnread && !isPending && !error && !markAllAsReadMutation.isPending;

  const handleMarkAllAsRead = () => {
    if (canMarkAll) markAllAsReadMutation.mutate();
  };

  return (
    <Page>
      <PageHeader
        title="Notifications"
        actions={
          <PageHeaderAction
            onClick={handleMarkAllAsRead}
            disabled={!canMarkAll}
            aria-busy={markAllAsReadMutation.isPending}
            className="max-w-32 whitespace-normal px-2 text-right leading-tight sm:max-w-none"
          >
            {markAllAsReadMutation.isPending ? "Marking as read..." : "Mark all as read"}
          </PageHeaderAction>
        }
      >
        <Tabs
          options={filters}
          value={filter}
          onValueChange={setFilter}
          label="Notification filters"
        />
      </PageHeader>
      <PageContent className="space-y-3">
        {markAllAsReadMutation.error && (
          <ErrorState
            title="Couldn't mark notifications as read"
            message={markAllAsReadMutation.error.message}
            onRetry={canMarkAll ? handleMarkAllAsRead : undefined}
          />
        )}
        <NotificationsList
          notifications={notifications}
          isLoading={isPending || (!!error && isFetching)}
          error={isFetching ? null : error}
          unreadOnly={filter === "unread"}
          onRetry={() => { void refetch(); }}
        />
      </PageContent>
    </Page>
  );
}
